import {
  createContext,
  PropsWithChildren,
  useEffect,
  useMemo,
  useRef
} from 'react'
import { SwitchTransition, Transition } from 'react-transition-group'
import gsap from 'gsap'
import ScrollTrigger from 'gsap/dist/ScrollTrigger'
import { useRouter } from 'next/router'

import { useLoaderStore } from '@/store/loader'

import { fadeAnimation } from './animations'
import { WPPageData } from '@/types'

export const TransitionContext = createContext<undefined>(undefined)

const TransitionProvider = ({
  children
}: PropsWithChildren<{ pageProps: WPPageData }>) => {
  const router = useRouter()
  const active = useRef(false)
  const nodeRef = useRef<HTMLElement>(null!)
  const contextValue = useMemo(() => undefined, [])
  const setLoaderState = useLoaderStore((state) => state.setLoaderState)

  const handleEnterTransition = () => {
    gsap.set(nodeRef.current, { opacity: 0 })
    active.current = true
  }

  const handleExitTransition = () => {
    active.current = false
    setLoaderState({ heroCanPlay: false })
    setLoaderState({ isPosterReady: false })
  }

  const transitionListeners = (done: () => void) => {
    fadeAnimation(nodeRef.current, active.current, done)
  }

  useEffect(() => {
    ScrollTrigger.clearScrollMemory('manual')

    router.beforePopState((state) => {
      state.options.scroll = false
      return true
    })
  }, [router])

  return (
    <TransitionContext.Provider value={contextValue}>
      <SwitchTransition mode="out-in">
        <Transition
          key={router.asPath}
          nodeRef={nodeRef}
          onEnter={handleEnterTransition}
          onExit={handleExitTransition}
          addEndListener={transitionListeners}
          unmountOnExit
        >
          <main ref={nodeRef} className="static">
            {children}
          </main>
        </Transition>
      </SwitchTransition>
    </TransitionContext.Provider>
  )
}

export default TransitionProvider
